/**
 * Edit-form rules for the S&D transaction detail modal.
 *
 * The modal seeds its edit form from whatever transaction object it holds at the
 * time. List rows are column-pruned (`getListAttributes` returns only the user's
 * configured columns plus a small essential set), so fields the user hasn't put
 * in their table — `duns_gl_branch`, `supplier_sell_location`, `buyer_product_no`,
 * `upc_code` — arrive as `undefined` and seed as ''. Diffing that '' against the
 * full record (which loads a moment later) reads as "the user cleared this field"
 * and silently blanks it.
 *
 * That is not hypothetical: it wiped the vendor catalog number (RDBPC → the 844D
 * `LIN*VC` element) on three Schneider claims, which Schneider's translator then
 * rejected (batches 30315 / 30318, Aug 2026), and 61 edits org-wide show the same
 * pattern.
 *
 * The rule enforced here: **only fields the user actually edited are ever sent.**
 * An untouched field cannot be written no matter what the form was seeded from.
 */

import { parseDecimalInput } from './transactionEditableFields';

export interface PricingFormData {
  claimAmount: string;
  buyerCost: string;
  coreCostOverride: string;
  contractNo: string;
  dunsLocation: string;
  suppSellLoc: string;
  upcCode: string;
  buyerProductNo: string;
}

export type PricingFormField = keyof PricingFormData;

/**
 * Form field → the snake_case API key it writes, its human label (for the
 * validation message), and whether it is a decimal.
 *
 * `claimAmount` is deliberately absent: the backend recalculates it via OPE84440
 * from the pricing fields, so sending it would fight the AS/400 formula.
 */
const FIELD_TO_UPDATE: Record<
  Exclude<PricingFormField, 'claimAmount'>,
  { apiField: string; label: string; decimal?: true }
> = {
  buyerCost: {
    apiField: 'unit_price_wholesale',
    label: 'Wholesale Price',
    decimal: true,
  },
  coreCostOverride: {
    apiField: 'unit_price_quoted',
    label: 'Quoted Price',
    decimal: true,
  },
  contractNo: { apiField: 'supplier_contract_no', label: 'Contract No' },
  dunsLocation: { apiField: 'duns_gl_branch', label: 'DUNS Location' },
  suppSellLoc: {
    apiField: 'supplier_sell_location',
    label: 'Supp. Sell Loc',
  },
  upcCode: { apiField: 'upc_code', label: 'UPC Code' },
  buyerProductNo: {
    apiField: 'buyer_product_no',
    label: 'Buyer Product No',
  },
};

const UPDATABLE_FIELDS = Object.keys(FIELD_TO_UPDATE) as Exclude<
  PricingFormField,
  'claimAmount'
>[];

export interface PricingUpdatePlan {
  updates: Record<string, unknown>;
  /** Labels of touched decimal fields whose text isn't a complete number. */
  invalidFields: string[];
}

/**
 * Build the update payload for `updateStagedTransaction`.
 *
 * A field is included only when the user touched it AND its value actually
 * differs from the record's current value — so a touched-then-reverted field is
 * still a no-op. `current` must be the form projection of the FULL transaction
 * record (`transactionToFormData(effectiveTransaction)`), not the list row.
 *
 * A touched price that isn't a complete decimal is reported in `invalidFields`
 * and omitted from `updates`; the caller must block the save rather than write
 * a truncated (or null) price. See `parseDecimalInput`.
 */
export function buildPricingUpdates(
  form: PricingFormData,
  current: PricingFormData,
  touched: ReadonlySet<PricingFormField>,
): PricingUpdatePlan {
  const updates: Record<string, unknown> = {};
  const invalidFields: string[] = [];
  for (const field of UPDATABLE_FIELDS) {
    if (!touched.has(field)) continue;
    if (form[field] === current[field]) continue;
    const { apiField, label, decimal } = FIELD_TO_UPDATE[field];
    if (!decimal) {
      updates[apiField] = form[field] || null;
      continue;
    }
    const parsed = parseDecimalInput(form[field]);
    if (!parsed.ok) {
      invalidFields.push(label);
      continue;
    }
    updates[apiField] = parsed.value;
  }
  return { updates, invalidFields };
}

/**
 * Refresh the fields the user hasn't touched from a newly-arrived record,
 * leaving their in-progress edits alone.
 *
 * Called when the full transaction lands while the form is already open in edit
 * mode — it repairs a form seeded from a pruned list row, both for display and
 * for the save diff. Returns the same reference when nothing changed so it
 * doesn't provoke a needless re-render.
 */
export function mergeUntouchedPricingFields(
  form: PricingFormData,
  fresh: PricingFormData,
  touched: ReadonlySet<PricingFormField>,
): PricingFormData {
  let changed = false;
  const merged = { ...form };
  for (const field of Object.keys(form) as PricingFormField[]) {
    if (touched.has(field)) continue;
    if (form[field] === fresh[field]) continue;
    merged[field] = fresh[field];
    changed = true;
  }
  return changed ? merged : form;
}
